/**
 * PackagesPanel — apt: list --upgradable, update, upgrade (simulate/real), install.
 *
 * Backend: /api/v1/devops/packages/{upgradable,update,upgrade,install}.
 */

import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  Package,
  RefreshCw,
  Loader2,
  Download,
  Play,
  Plus,
  ShieldAlert,
} from "lucide-react";
import {
  fetchUpgradablePackages,
  aptUpdate,
  aptUpgrade,
  aptInstall,
} from "../../lib/api";
import { cn } from "../../lib/cn";

export function PackagesPanel() {
  const [output, setOutput] = useState("");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [upgradeOutput, setUpgradeOutput] = useState<string | null>(null);
  const [confirmRealUpgrade, setConfirmRealUpgrade] = useState(false);
  const [installPkgs, setInstallPkgs] = useState("");
  const [confirmInstall, setConfirmInstall] = useState<string[] | null>(null);

  async function loadUpgradable() {
    try {
      setLoading(true);
      const r = await fetchUpgradablePackages();
      setOutput(r.output);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadUpgradable();
  }, []);

  async function doUpdate() {
    setBusy("update");
    setError(null);
    try {
      const r = await aptUpdate();
      setUpgradeOutput("[apt update]\n" + r.output);
      await loadUpgradable();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  }

  async function doUpgrade(simulate: boolean, confirmed: boolean) {
    setBusy(simulate ? "simulate" : "upgrade");
    setError(null);
    try {
      const r = await aptUpgrade({ simulate, confirm: confirmed });
      setUpgradeOutput(`[${simulate ? "SIMULATE" : "REAL"}] ` + r.output);
      setConfirmRealUpgrade(false);
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      if (msg.includes(" 409 ")) {
        setConfirmRealUpgrade(true);
      } else {
        setError(msg);
      }
    } finally {
      setBusy(null);
    }
  }

  async function doInstall(confirmed: boolean) {
    const pkgs = installPkgs
      .split(/\s+/)
      .map((p) => p.trim())
      .filter((p) => p);
    if (pkgs.length === 0) return;
    setBusy("install");
    setError(null);
    try {
      const r = await aptInstall(pkgs, { confirm: confirmed });
      setUpgradeOutput("[apt install]\n" + r.output);
      setConfirmInstall(null);
      await loadUpgradable();
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      if (msg.includes(" 409 ")) {
        setConfirmInstall(pkgs);
      } else {
        setError(msg);
      }
    } finally {
      setBusy(null);
    }
  }

  const upgradableLines = output
    ? output.split("\n").filter((l) => l.includes("/") && !l.startsWith("Listing"))
    : [];

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 flex-wrap">
        <button
          type="button"
          onClick={doUpdate}
          disabled={busy === "update"}
          className="px-2 py-1.5 rounded border border-accent-500/40 text-accent-500 hover:bg-accent-500/10 disabled:opacity-50 inline-flex items-center gap-1 text-small"
        >
          {busy === "update" ? <Loader2 className="size-3.5 animate-spin" /> : <Download className="size-3.5" />}
          apt update
        </button>
        <button
          type="button"
          onClick={() => doUpgrade(true, false)}
          disabled={busy === "simulate"}
          className="px-2 py-1.5 rounded border border-border/60 hover:bg-bg-subtle text-fg-muted inline-flex items-center gap-1 text-small disabled:opacity-50"
        >
          {busy === "simulate" ? <Loader2 className="size-3.5 animate-spin" /> : <Package className="size-3.5" />}
          apt upgrade -s (simulate)
        </button>
        <button
          type="button"
          onClick={() => doUpgrade(false, false)}
          disabled={busy === "upgrade"}
          className="px-2 py-1.5 rounded border border-warning/40 text-warning hover:bg-warning/10 inline-flex items-center gap-1 text-small disabled:opacity-50"
        >
          {busy === "upgrade" ? <Loader2 className="size-3.5 animate-spin" /> : <Play className="size-3.5" />}
          apt upgrade (real)
        </button>
        <button type="button" onClick={loadUpgradable} className="px-2 py-1.5 rounded border border-border/60 hover:bg-bg-subtle text-small">
          <RefreshCw className="size-3.5" />
        </button>
        <span className="text-small text-fg-muted ml-auto">
          {upgradableLines.length > 0 ? `${upgradableLines.length} upgradable` : "all up to date"}
        </span>
      </div>

      {confirmRealUpgrade && (
        <ConfirmDialog
          title="apt upgrade обновит все пакеты на хосте"
          subtitle="Это может перезапустить сервисы. Сначала рекомендуется simulate."
          onConfirm={() => doUpgrade(false, true)}
          onCancel={() => setConfirmRealUpgrade(false)}
        />
      )}

      {error && <div className="rounded border border-danger/40 bg-danger/5 p-2 text-small text-danger">{error}</div>}

      {/* Список upgradable */}
      <div className="rounded-modal border border-border/60 bg-bg-card/40 overflow-hidden">
        <div className="px-3 py-2 border-b border-border/40 bg-bg-subtle/40 text-[11px] uppercase tracking-wider font-mono text-fg-subtle">
          Upgradable packages
        </div>
        {loading ? (
          <div className="p-3 text-fg-muted text-small">
            <Loader2 className="size-4 animate-spin inline mr-2" /> загрузка…
          </div>
        ) : upgradableLines.length === 0 ? (
          <div className="p-3 text-fg-muted text-small">Все пакеты в актуальном состоянии.</div>
        ) : (
          <pre className="p-3 text-[11px] font-mono whitespace-pre-wrap text-fg max-h-[400px] overflow-auto">
            {upgradableLines.join("\n")}
          </pre>
        )}
      </div>

      {/* Install form */}
      <div className="rounded-xl border border-border/60 bg-bg-card/40 p-3 space-y-2">
        <div className="text-small font-medium flex items-center gap-1.5">
          <Plus className="size-3.5" />
          Установить пакеты (apt-get install)
        </div>
        <div className="flex gap-2">
          <input
            type="text"
            value={installPkgs}
            onChange={(e) => setInstallPkgs(e.target.value)}
            placeholder="htop fail2ban tmux"
            className="flex-1 px-2 py-1.5 rounded border border-border/60 bg-bg-card text-small font-mono"
          />
          <button
            type="button"
            onClick={() => doInstall(false)}
            disabled={busy === "install" || !installPkgs.trim()}
            className="px-3 py-1.5 rounded text-small font-medium text-white disabled:opacity-50"
            style={{ backgroundImage: "var(--grad-cta)" }}
          >
            {busy === "install" ? <Loader2 className="size-3.5 animate-spin inline" /> : "Установить"}
          </button>
        </div>
        {confirmInstall && (
          <ConfirmDialog
            title={`Установить: ${confirmInstall.join(", ")}`}
            subtitle="apt -y install"
            onConfirm={() => doInstall(true)}
            onCancel={() => setConfirmInstall(null)}
          />
        )}
      </div>

      {/* Вывод последней операции */}
      {upgradeOutput && (
        <div className="rounded-modal border border-border/60 bg-bg-card/40 overflow-hidden">
          <div className="px-3 py-2 border-b border-border/40 bg-bg-subtle/40 text-[11px] uppercase tracking-wider font-mono text-fg-subtle">
            Output
          </div>
          <pre className="p-3 text-[11px] font-mono whitespace-pre-wrap text-fg max-h-[400px] overflow-auto">
            {upgradeOutput}
          </pre>
        </div>
      )}
    </div>
  );
}

function ConfirmDialog({
  title,
  subtitle,
  onConfirm,
  onCancel,
}: {
  title: string;
  subtitle?: string;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 4 }}
      animate={{ opacity: 1, y: 0 }}
      className="rounded-xl border border-warning/40 bg-warning/5 p-3 flex items-start gap-2"
    >
      <ShieldAlert className="size-4 mt-0.5 text-warning shrink-0" />
      <div className="flex-1">
        <div className="font-medium text-small">{title}</div>
        {subtitle && <div className="text-[12px] text-fg-muted mt-0.5">{subtitle}</div>}
        <div className="mt-2 flex gap-2">
          <button
            type="button"
            onClick={onConfirm}
            className="px-3 py-1.5 rounded-md text-small font-medium text-white"
            style={{ backgroundImage: "var(--grad-cta)" }}
          >
            Подтверждаю
          </button>
          <button
            type="button"
            onClick={onCancel}
            className="px-3 py-1.5 rounded-md text-small border border-border/60"
          >
            Отмена
          </button>
        </div>
      </div>
    </motion.div>
  );
}
